// Browser-side live animation renderer. Mirrors the server animation_engine
// so the in-editor preview matches the exported WebM/MP4 — including the
// per-word effect overrides (fill/stroke/shadow/glow/3D/background).

import { resolveWordStyle, resolveObjWordStyle, applyCase } from "@/lib/style";
import { teluguFamilyFor } from "@/lib/fonts";
import { beatAt } from "@/lib/beats";
import { trackPos, camAt, camPoint, motionOffset } from "@/lib/motion";
import { fxOf, fxCollect, drawFxBack, drawFxFront, drawFxText, fxDrift, drawKnockout, isKnockout } from "@/lib/captionFx";

const POS_Y = { top: 0.14, "upper-middle": 0.30, center: 0.5, "lower-middle": 0.72, "lower-third": 0.72, bottom: 0.86 };
const easeOutCubic = (p) => 1 - Math.pow(1 - p, 3);
const easeOutBack = (p) => { const c1 = 1.70158, c3 = 2.70158; return 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2); };
const clamp = (v, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, v));

function scriptFamily(text) {
  for (const ch of text) {
    const o = ch.codePointAt(0);
    if (o >= 0x0900 && o <= 0x097F) return "Noto Sans Devanagari";
    if (o >= 0x0C00 && o <= 0x0C7F) return "Noto Sans Telugu";
    if (o >= 0x0B80 && o <= 0x0BFF) return "Noto Sans Tamil";
    if (o >= 0x0D00 && o <= 0x0D7F) return "Noto Sans Malayalam";
    if (o >= 0x0C80 && o <= 0x0CFF) return "Noto Sans Kannada";
  }
  return null;
}

function transformFor(anim, enterP, exitP, w, h, idx) {
  let scale = 1, dx = 0, dy = 0, alpha = 1, rot = 0, blur = 0, reveal = 1;
  const e = clamp(enterP);
  switch (anim) {
    case "fade": alpha = e; break;
    case "pop": scale = 0.5 + 0.5 * easeOutBack(e); alpha = clamp(e * 1.5); break;
    case "bouncy": scale = 0.6 + 0.5 * easeOutBack(e); alpha = clamp(e * 1.6); break;
    case "punch": scale = 1.5 - 0.5 * easeOutCubic(e); rot = (1 - e) * 8 * (idx % 2 ? -1 : 1); alpha = clamp(e * 2); break;
    case "slide-up": dy = (1 - easeOutCubic(e)) * h * 0.06; alpha = e; break;
    case "slide-down": dy = -(1 - easeOutCubic(e)) * h * 0.06; alpha = e; break;
    case "slide-left": dx = (1 - easeOutCubic(e)) * w * 0.10; alpha = e; break;
    case "slide-right": dx = -(1 - easeOutCubic(e)) * w * 0.10; alpha = e; break;
    case "typewriter": reveal = e; alpha = e > 0 ? 1 : 0; break;
    case "kinetic": scale = 0.7 + 0.3 * easeOutBack(e); dy = (1 - easeOutCubic(e)) * h * 0.03 * (idx % 2 ? -1 : 1); alpha = clamp(e * 1.5); break;
    case "motion-blur": dx = (1 - easeOutCubic(e)) * w * 0.12; blur = (1 - e) * 14; alpha = e; break;
    case "minimal": scale = 0.92 + 0.08 * easeOutCubic(e); alpha = e; break;
    case "word-blur": case "blur": alpha = e; blur = (1 - e) * 18; scale = 0.98 + 0.02 * easeOutCubic(e); break;
    case "rise": case "staggered-rise": dy = (1 - easeOutCubic(e)) * h * 0.05; blur = (1 - e) * 6; alpha = e; break;
    case "drop": dy = -(1 - easeOutCubic(e)) * h * 0.05; alpha = e; break;
    case "scale": scale = 0.8 + 0.2 * easeOutCubic(e); alpha = clamp(e * 1.3); break;
    case "wave": dy = (1 - easeOutCubic(e)) * h * 0.05 * (idx % 2 ? -1 : 1); alpha = e; break;
    case "flip": scale = 0.3 + 0.7 * easeOutBack(e); rot = (1 - e) * 22 * (idx % 2 ? -1 : 1); alpha = clamp(e * 1.6); break;
    case "none": scale = 1; alpha = 1; break;
    default: alpha = e;
  }
  const xe = clamp(exitP);
  alpha *= xe;
  if (["pop", "bouncy", "punch"].includes(anim)) scale *= 0.85 + 0.15 * xe;
  return { scale, dx, dy, alpha: clamp(alpha), rot, blur, reveal };
}

function fontFor(style, text, scale) {
  const script = scriptFamily(text);
  // Telugu keeps the chosen font when it has Telugu letters, else uses the closest-looking Telugu font.
  const fam = script === "Noto Sans Telugu" ? teluguFamilyFor(style.fontFamily) : (script || style.fontFamily);
  const px = Math.max(12, style.fontSize * scale);
  const italic = style.fontStyle === "italic" ? "italic " : "";
  return `${italic}${style.fontWeight || 700} ${px}px "${fam}"${script && fam !== script ? `, "${script}"` : ""}, sans-serif`;
}

// Word-timing engine — mirrors backend _split_words_proportional.
function splitWordsProportional(text, start, end, baseId) {
  const toks = (text || "").split(/\s+/).filter(Boolean);
  if (!toks.length) return [];
  const totalLen = toks.reduce((a, w) => a + w.length, 0) || 1;
  const dur = Math.max(0.01, (+end) - (+start));
  let cur = +start;
  return toks.map((w, i) => {
    const seg = dur * (w.length / totalLen);
    const o = { word: w, start: +(cur.toFixed(3)), end: +((cur + seg).toFixed(3)), word_id: `${baseId || "w"}-${i}` };
    cur += seg;
    return o;
  });
}

// Two-font distribution — mirrors backend _apply_font_mode.
function applyFontMode(config, idx, isHi, style) {
  const mode = (config && config.fontMode) || "single";
  if (mode === "single" || !config?.fontFamilyB) return style;
  let useB = false;
  if (mode === "alternate") useB = idx % 2 === 1;
  else if (mode === "first-second") useB = idx >= 1;
  else if (mode === "highlight") useB = !!isHi;
  if (!useB) return style;
  return {
    ...style,
    fontFamily: config.fontFamilyB,
    fontWeight: config.fontWeightB ? +config.fontWeightB : style.fontWeight,
    fontStyle: config.fontStyleB || style.fontStyle,
  };
}

function wrapTokens(ctx, tokens, maxWidth, scale) {
  const lines = []; let cur = [], curW = 0;
  for (const t of tokens) {
    ctx.font = fontFor(t.style, t.text, scale);
    const tw = ctx.measureText(t.text).width;
    const sp = ctx.measureText(" ").width;
    const add = tw + (cur.length ? sp : 0);
    if (cur.length && curW + add > maxWidth) { lines.push(cur); cur = [t]; curW = tw; }
    else { cur.push(t); curW += add; }
  }
  if (cur.length) lines.push(cur);
  return lines;
}

function roundRectPath(ctx, x, y, w, h, r) {
  r = Math.max(0, Math.min(r, Math.min(w, h) / 2));
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawBackground(ctx, s, x, y, w, h, scale, baseAlpha) {
  const px = s.fontSize * scale;
  const padx = s.bgPaddingX * px, pady = s.bgPaddingY * px;
  const bx = x - padx, by = y - pady, bw = w + padx * 2, bh = h + pady * 2;
  const radius = s.bgRadius * Math.min(bw, bh) / 2;
  ctx.save();
  ctx.globalAlpha = baseAlpha * clamp(s.bgOpacity);
  if (s.bgType === "gradient") {
    const a = (s.bgAngle * Math.PI) / 180;
    const gx = Math.cos(a), gy = Math.sin(a);
    const g = ctx.createLinearGradient(bx + bw / 2 - gx * bw / 2, by + bh / 2 - gy * bh / 2, bx + bw / 2 + gx * bw / 2, by + bh / 2 + gy * bh / 2);
    g.addColorStop(0, s.bgColor); g.addColorStop(1, s.bgColor2);
    ctx.fillStyle = g;
  } else {
    ctx.fillStyle = s.bgColor;
  }
  roundRectPath(ctx, bx, by, bw, bh, radius);
  ctx.fill();
  ctx.restore();
}

function drawTokenEffects(ctx, s, text, x, y, scale, baseAlpha) {
  const op = clamp(s.opacity ?? 1);
  // Outer glow
  if (s.glowEnabled) {
    ctx.save();
    ctx.globalAlpha = baseAlpha * clamp(s.glowOpacity) * op;
    ctx.filter = `blur(${Math.max(0, s.glowBlur * scale * 0.6)}px)`;
    ctx.fillStyle = s.glowColor;
    ctx.fillText(text, x, y); ctx.fillText(text, x, y);
    ctx.restore();
  }
  // Drop shadow
  if (s.shadowEnabled) {
    ctx.save();
    ctx.globalAlpha = baseAlpha * clamp(s.shadowOpacity) * op;
    if (s.shadowBlur > 0.4) ctx.filter = `blur(${s.shadowBlur * scale}px)`;
    ctx.fillStyle = s.shadowColor;
    ctx.fillText(text, x + s.shadowDx, y + s.shadowDy);
    ctx.restore();
  }
  ctx.save();
  ctx.globalAlpha = baseAlpha * op;
  // 3D extrude
  if (s.threeDEnabled && s.threeDDepth > 0) {
    ctx.fillStyle = s.threeDColor;
    for (let i = s.threeDDepth; i > 0; i--) ctx.fillText(text, x + i * 0.7, y + i * 0.7);
  }
  // Stroke
  if (s.strokeEnabled && s.strokeWidth > 0) {
    ctx.lineWidth = s.strokeWidth; ctx.strokeStyle = s.strokeColor; ctx.lineJoin = "round";
    ctx.strokeText(text, x, y);
  }
  // Fill (solid or gradient)
  if (s.fillGradientEnabled) {
    const tw = ctx.measureText(text).width;
    const px = s.fontSize * scale;
    const a = ((s.fillGradientAngle ?? 90) * Math.PI) / 180;
    const gx = Math.cos(a), gy = Math.sin(a);
    const g = ctx.createLinearGradient(x + tw / 2 - gx * tw / 2, y - gy * px / 2, x + tw / 2 + gx * tw / 2, y + gy * px / 2);
    g.addColorStop(0, s.fillGradientFrom || "#00F0FF");
    g.addColorStop(1, s.fillGradientTo || "#8B5CF6");
    ctx.fillStyle = g;
  } else {
    ctx.fillStyle = s.fillColor;
  }
  ctx.fillText(text, x, y);
  ctx.restore();
}

function drawTokens(ctx, tokens, lineHeight, align, cx, cy, maxWidth, tf, scale) {
  ctx.save();
  ctx.textBaseline = "middle";
  if (tf.blur > 0.5) ctx.filter = `blur(${tf.blur}px)`;
  const lines = wrapTokens(ctx, tokens, maxWidth, scale);
  const maxPx = Math.max(...tokens.map((t) => t.style.fontSize * scale), 1);
  const lineH = maxPx * lineHeight;
  const totalH = lines.length * lineH;

  ctx.translate(cx + tf.dx, cy + tf.dy);
  ctx.scale(tf.scale, tf.scale);
  ctx.rotate((tf.rot * Math.PI) / 180);

  // Pre-pass: measure each line's width + x-offset so we can (a) draw ONE background
  // container behind the whole caption and (b) still lay out tokens per line.
  const lineMeta = [];
  for (const ln of lines) {
    let lineW = 0;
    for (const t of ln) { ctx.font = fontFor(t.style, t.text, scale); lineW += ctx.measureText(t.text).width; }
    ctx.font = fontFor(ln[0].style, " ", scale);
    const spaceW = ctx.measureText(" ").width;
    lineW += spaceW * (ln.length - 1);
    const x = align === "left" ? -maxWidth / 2 : align === "right" ? maxWidth / 2 - lineW : -lineW / 2;
    lineMeta.push({ ln, lineW, spaceW, x });
  }

  // Sentence/container background: single box covering the full caption block.
  const bgStyle = (tokens.find((t) => t.style.bgEnabled) || tokens[0]).style;
  const containerBg = bgStyle.bgEnabled && bgStyle.bgMode !== "word";
  if (containerBg) {
    let minL = Infinity, maxR = -Infinity;
    for (const m of lineMeta) { minL = Math.min(minL, m.x); maxR = Math.max(maxR, m.x + m.lineW); }
    if (!isFinite(minL)) { minL = -maxWidth / 2; maxR = maxWidth / 2; }
    const top = -totalH / 2, blockW = Math.max(1, maxR - minL);
    drawBackground(ctx, bgStyle, minL, top, blockW, totalH, scale, tf.alpha);
  }

  let y = -totalH / 2 + lineH / 2;
  for (const { ln, spaceW, x: lineX } of lineMeta) {
    // Per-word backgrounds only when explicitly selected.
    if (!containerBg) {
      let bx = lineX;
      for (const t of ln) {
        ctx.font = fontFor(t.style, t.text, scale);
        const tw = ctx.measureText(t.text).width;
        if (t.style.bgEnabled) {
          const th = t.style.fontSize * scale;
          drawBackground(ctx, t.style, bx, y - th / 2, tw, th, scale, tf.alpha);
        }
        bx += tw + ctx.measureText(" ").width;
      }
    }
    let x = lineX;
    for (const t of ln) {
      ctx.font = fontFor(t.style, t.text, scale);
      const tw = ctx.measureText(t.text).width;
      drawTokenEffects(ctx, t.style, t.text, x, y, scale, tf.alpha);
      x += tw + spaceW;
    }
    y += lineH;
  }
  ctx.restore();
}

// True per-letter reveal (Fade / Scale) — mirrors backend _render_letter_block.
function drawLetterBlock(ctx, text, style, mode, tObj, oStart, oEnd, cx, cy, maxWidth, scale, lineHeight, align) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  if (!words.length) return;
  ctx.save();
  ctx.textBaseline = "middle";
  ctx.font = fontFor(style, text, scale);
  const spaceW = ctx.measureText(" ").width;
  const px = style.fontSize * scale;
  const lineH = px * lineHeight;
  // wrap
  const lines = []; let cur = [], curW = 0;
  for (const w of words) {
    const ww = ctx.measureText(w).width;
    const add = ww + (cur.length ? spaceW : 0);
    if (cur.length && curW + add > maxWidth) { lines.push(cur); cur = [w]; curW = ww; }
    else { cur.push(w); curW += add; }
  }
  if (cur.length) lines.push(cur);
  const nChars = Math.max(1, words.reduce((a, w) => a + w.length, 0));
  const dur = Math.max(0.25, (+oEnd) - (+oStart));
  const step = Math.min(dur * 0.75, nChars * 0.05) / nChars;
  const charDur = Math.max(0.09, step * 2.2);
  const grad = !!style.fillGradientEnabled;
  const c1 = style.fillGradientFrom || "#00F0FF", c2 = style.fillGradientTo || "#8B5CF6";
  const lineWs = lines.map((ln) => ln.reduce((a, w) => a + ctx.measureText(w).width, 0) + spaceW * (ln.length - 1));
  const maxLW = Math.max(...lineWs);
  const totalH = lineH * lines.length;
  let ci = 0;
  let y = cy - totalH / 2 + lineH / 2;
  lines.forEach((ln, li) => {
    const lw = lineWs[li];
    let x = align === "left" ? cx - maxWidth / 2 : align === "right" ? cx + maxWidth / 2 - lw : cx - lw / 2;
    ln.forEach((w, wi) => {
      if (wi > 0) x += spaceW;
      for (const ch of w) {
        const cw = ctx.measureText(ch).width;
        const p = Math.max(0, Math.min(1, (tObj - (oStart + ci * step)) / charDur));
        if (p > 0) {
          const sc = mode === "scale" ? (0.35 + 0.65 * easeOutBack(Math.min(1, p))) : 1;
          const col = grad ? lerpColor(c1, c2, ci / Math.max(1, nChars - 1)) : style.fillColor;
          ctx.save();
          ctx.globalAlpha = p;
          ctx.translate(x + cw / 2, y);
          ctx.scale(sc, sc);
          if (style.strokeEnabled && style.strokeWidth > 0) {
            ctx.lineWidth = style.strokeWidth * scale * 2;
            ctx.strokeStyle = style.strokeColor;
            ctx.lineJoin = "round";
            ctx.strokeText(ch, 0, 0);
          }
          ctx.fillStyle = col;
          ctx.fillText(ch, 0, 0);
          ctx.restore();
        }
        ci++;
        x += cw;
      }
    });
    y += lineH;
  });
  ctx.restore();
}

function lerpColor(a, b, t) {
  const pa = hexToRgb(a), pb = hexToRgb(b);
  return `rgb(${Math.round(pa[0] + (pb[0] - pa[0]) * t)},${Math.round(pa[1] + (pb[1] - pa[1]) * t)},${Math.round(pa[2] + (pb[2] - pa[2]) * t)})`;
}
function hexToRgb(h) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h || "#ffffff");
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [255, 255, 255];
}

const totalDuration = (segments) => {
  let end = 0;
  segments.forEach((s) => { end = Math.max(end, s.end); (s.words || []).forEach((w) => (end = Math.max(end, w.end))); });
  return end + 0.4;
};

export function drawFrame(ctx, t, W, H, segments, config, opts = {}) {
  if (!opts.noClear) ctx.clearRect(0, 0, W, H);
  if (!segments || !segments.length) return;
  const mode = config.animation === "karaoke" ? "karaoke" : config.animationTarget;
  const scale = (W / 1080) * (mode !== "word" ? 0.8 : 1);
  const lineHeight = config.lineHeight || 1.15;
  const align = config.align || "center";
  try { ctx.letterSpacing = `${(config.letterSpacing || 0) * scale}px`; } catch { /* older browsers */ }
  const maxW = W * 0.84;
  const cx = W * (config.posX ?? 0.5);
  const cy = H * (config.posY != null ? config.posY : (POS_Y[config.position] ?? 0.72));
  const enterDur = Math.max(0.05, config.animEnterDur || 0.16), exitDur = 0.12;
  const highlightSet = new Set(config.highlightWordIds || []);

  if (mode === "word") {
    let idx = 0;
    segments.forEach((seg) => (seg.words || []).forEach((w) => {
      if (t >= w.start - 0.05 && t <= w.end + exitDur) {
        const enterP = (t - w.start) / enterDur;
        const exitP = t > w.end ? 1 - (t - w.end) / exitDur : 1;
        let style = resolveWordStyle(config, w.word_id, highlightSet.has(w.word_id));
        style = applyFontMode(config, idx, highlightSet.has(w.word_id), style);
        const anim = style.animation || config.animation;
        const tf = transformFor(anim, enterP, exitP, W, H, idx);
        drawTokens(ctx, [{ text: applyCase(w.word, style.textCase, 0), style }], lineHeight, align, cx, cy, maxW, tf, scale);
      }
      idx++;
    }));
  } else {
    const units = [];
    segments.forEach((seg) => {
      const ws = seg.words || [];
      if (!ws.length) return;
      if (mode === "phrase") {
        for (let i = 0; i < ws.length; i += 3) {
          const chunk = ws.slice(i, i + 3);
          units.push({ start: chunk[0].start, end: chunk[chunk.length - 1].end, words: chunk, karaoke: false });
        }
      } else {
        units.push({ start: ws[0].start, end: ws[ws.length - 1].end, words: ws, karaoke: mode === "karaoke" });
      }
    });
    units.forEach((u, i) => {
      if (t >= u.start - 0.05 && t <= u.end + 0.15) {
        const enterP = (t - u.start) / enterDur;
        const tf = transformFor(u.karaoke ? "fade" : config.animation, enterP, 1, W, H, i);
        const tokens = u.words.map((w, j) => {
          const active = t >= w.start && t <= w.end;
          const pinned = highlightSet.has(w.word_id);
          const lit = u.karaoke ? (t >= w.start || pinned) : (active || pinned);
          return { text: applyCase(w.word, config.textCase, j), style: applyFontMode(config, j, lit, resolveWordStyle(config, w.word_id, lit)) };
        });
        drawTokens(ctx, tokens, lineHeight, align, cx, cy, maxW, tf, scale);
      }
    });
  }

  // Emoji pops — timestamp-anchored (explicit time, fallback to a word's start)
  drawEmoji(ctx, t, W, H, config, segments);
}

// Emoji pops — shared by transcript and caption-timeline preview.
function drawEmoji(ctx, t, W, H, config, segments) {
  const triggers = config.emojiTriggers || [];
  if (!triggers.length) return;
  const cx = W * (config.posX ?? 0.5);
  const cy = H * (config.posY != null ? config.posY : (POS_Y[config.position] ?? 0.72));
  const byId = {};
  (segments || []).forEach((s) => (s.words || []).forEach((w) => (byId[w.word_id] = w)));
  const emojiPx = ((config.fontSize / 1080) * W) * 1.15;
  triggers.forEach((tr) => {
    if (!tr.emoji) return;
    let t0 = tr.time, tend;
    if (t0 == null) {
      const w = byId[tr.word_id];
      if (!w) return;
      t0 = w.start; tend = w.end + 0.6;
    } else {
      tend = t0 + (tr.dur || 0.9);
    }
    if (!(t >= t0 - 0.05 && t <= tend)) return;
    const ep = (t - t0) / 0.22;
    const fadeStart = tend - 0.35;
    const alpha = t <= fadeStart ? 1 : clamp(1 - (t - fadeStart) / 0.35);
    const escale = ep < 1 ? 0.4 + 0.75 * easeOutBack(clamp(ep)) : 1;
    const ey = cy - ((config.fontSize / 1080) * W) * 1.25;
    ctx.save();
    ctx.globalAlpha = clamp(alpha);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.translate(cx, ey);
    ctx.scale(escale, escale);
    ctx.font = `${Math.round(emojiPx)}px "Noto Color Emoji", "Apple Color Emoji", "Segoe UI Emoji", sans-serif`;
    ctx.fillText(tr.emoji, 0, 0);
    ctx.restore();
  });
  ctx.textAlign = "left";
}

// Flatten the edited caption timeline into a render-ready object list.
// Mirrors backend build_caption_objects: draw later-listed layers FIRST so the
// TOP layer in the UI renders ON TOP; skip hidden layers and empty text.
function flattenCaptionObjects(layers) {
  if (!layers || !layers.length) return [];
  const objs = [];
  for (let i = layers.length - 1; i >= 0; i--) {
    const layer = layers[i];
    if (!layer || layer.hidden) continue;
    for (const o of (layer.objects || [])) {
      if (!(o.text || "").trim()) continue;
      objs.push({ ...o, __layerId: layer.id, __objId: o.id });
    }
  }
  objs.sort((a, b) => (a.start || 0) - (b.start || 0));
  return objs;
}

export function hasCaptionObjects(layers) {
  return flattenCaptionObjects(layers).length > 0;
}

// True if the caption timeline has ANY non-empty objects (hidden or visible).
// Used to decide whether to use the caption-driven renderer vs the transcript
// fallback — so hiding a layer blanks its captions instead of falling back to
// the transcript (which would still draw them).
export function hasAnyCaptionObjects(layers) {
  if (!layers || !layers.length) return false;
  return layers.some((l) => (l.objects || []).some((o) => (o.text || "").trim()));
}

// Measure a token block's on-screen size (canvas px) — mirrors wrapTokens/drawTokens.
function measureBlock(ctx, tokens, maxWidth, scale, lineHeight) {
  const lines = wrapTokens(ctx, tokens, maxWidth, scale);
  const maxPx = Math.max(...tokens.map((t) => t.style.fontSize * scale), 1);
  const lineH = maxPx * lineHeight;
  const totalH = lines.length * lineH;
  let maxW = 0;
  for (const ln of lines) {
    let lineW = 0;
    for (const t of ln) { ctx.font = fontFor(t.style, t.text, scale); lineW += ctx.measureText(t.text).width; }
    ctx.font = fontFor(ln[0].style, " ", scale);
    lineW += ctx.measureText(" ").width * (ln.length - 1);
    maxW = Math.max(maxW, lineW);
  }
  return { width: maxW, height: totalH };
}

// Resolve an object's steady-state center + block size (canvas px). Shared by
// the preview hit-testing / selection box.
function objectGeometry(ctx, o, W, H, config, scale, maxW, lineHeight) {
  const ws = (o.words && o.words.length) ? o.words : [{ word: o.text, start: o.start, end: o.end, word_id: o.word_id }];
  const single = ws.length === 1;
  const tokens = ws.map((w) => {
    let st = resolveObjWordStyle(config, o.style, w.word_id, false);
    if (!single) st = { ...st, fontSize: (st.fontSize || 90) * 0.82 };
    return { text: applyCase(w.word, st.textCase, 0), style: st };
  });
  const { width, height } = measureBlock(ctx, tokens, maxW, scale, lineHeight);
  const cx = W * (o.posX != null ? o.posX : (config.posX ?? 0.5));
  const cy = o.posY != null
    ? H * o.posY
    : (o.position ? H * (POS_Y[o.position] ?? 0.72)
      : H * (config.posY != null ? config.posY : (POS_Y[config.position] ?? 0.72)));
  const sc = o.scale ?? 1;
  return { cx, cy, width: width * sc, height: height * sc };
}

function drawSelectionBox(ctx, cx, cy, w, h, W) {
  const padX = Math.max(12, W * 0.012), padY = padX;
  const bw = w + padX * 2, bh = h + padY * 2;
  const x = cx - bw / 2, y = cy - bh / 2;
  const s = W / 1080;
  ctx.save();
  ctx.strokeStyle = "#00F0FF";
  ctx.lineWidth = Math.max(2, 2.5 * s);
  ctx.setLineDash([10 * s, 7 * s]);
  ctx.strokeRect(x, y, bw, bh);
  ctx.setLineDash([]);
  const hs = Math.max(6, 7 * s);
  ctx.fillStyle = "#00F0FF";
  [[x, y], [x + bw, y], [x, y + bh], [x + bw, y + bh]].forEach(([hx, hy]) => {
    ctx.fillRect(hx - hs / 2, hy - hs / 2, hs, hs);
  });
  ctx.restore();
}

// WYSIWYG preview driven by the EDITED caption timeline. Uses the SAME effective
// caption/style model as the server export (animation_engine.render_animation
// with caption_objects), so the in-editor preview == the final render.
// opts (optional): { hitboxes: [] filled with {layerId,objId,cx,cy,w,h}, selectedId }
export function drawCaptionFrame(ctx, t, W, H, layers, config, segments, opts = {}) {
  if (!opts.noClear) ctx.clearRect(0, 0, W, H);
  const hitboxes = opts.hitboxes;
  const objects = flattenCaptionObjects(layers);
  if (!objects.length) { drawEmoji(ctx, t, W, H, config, segments); return; }
  const scale = W / 1080; // object mode: per-object size handled per token
  const lineHeight = config.lineHeight || 1.15;
  const align = config.align || "center";
  const maxW = W * 0.84;
  const enterDur = Math.max(0.05, config.animEnterDur || 0.16), exitDur = 0.12;
  const highlightSet = new Set(config.highlightWordIds || []);
  const wordStyles = config.wordStyles || {};
  let selectedGeom = null;
  // Look effects (full-frame) run across the whole captioned span so they don't blink between captions.
  const fxs = opts.noFx ? [] : fxCollect(objects, config);
  let energy = 0;
  objects.forEach((o) => (o.words && o.words.length ? o.words : [o]).forEach((w) => {
    const ws = +(w.start ?? o.start) || 0;
    if (t >= ws && t - ws < 1.5) energy = Math.max(energy, Math.exp(-(t - ws) * 2.5));
  }));
  const spanS = Math.min(...objects.map((o) => +o.start || 0)), spanE = Math.max(...objects.map((o) => +o.end || 0));
  const fxOn = fxs.length && t >= spanS - 0.3 && t <= spanE + 0.6;
  // Real music beat (when analysed) drives the look effects; word timing is the fallback.
  const beat = fxs.length || objects.some((o) => fxOf(o, config)) ? beatAt(t) : null;
  if (beat) energy = Math.max(beat.pulse, beat.level * 0.7);
  if (fxOn) fxs.forEach((fx) => drawFxBack(ctx, fx, t, W, H, energy, beat));

  objects.forEach((o, oi) => {
    const start = +o.start || 0, end = +o.end || 0;
    const active = t >= start - 0.05 && t <= end + exitDur;
    if (!active) return;
    const enterP = (t - start) / enterDur;
    const exitP = t > end ? 1 - (t - end) / exitDur : 1;
    const ws0 = (o.words && o.words.length) ? o.words
      : ((config.wordByWord && (o.text || "").trim().split(/\s+/).filter(Boolean).length > 1)
        ? splitWordsProportional(o.text, start, end, o.word_id)
        : [{ word: o.text, start, end, word_id: o.word_id }]);
    const ws = ws0;
    const single = ws.length === 1;
    const w0id = ws[0] && ws[0].word_id;
    const wAnim = (wordStyles[w0id] || {}).animation;
    const anim = (single && wAnim) || o.animation || wAnim || config.animation;
    const wordByWord = !!config.wordByWord && !single && ws.length > 1;
    const tf = transformFor(wordByWord ? "none" : anim, wordByWord ? 1 : enterP, exitP, W, H, oi);
    tf.scale *= (o.scale ?? 1);
    tf.alpha *= (o.opacity ?? 1);
    tf.rot += (o.rotation ?? 0);
    const tokens = ws.map((w, j) => {
      const wid = w.word_id;
      const pinned = highlightSet.has(wid);
      const lit = single ? pinned : (((w.start ?? 0) <= t && t <= (w.end ?? 0)) || pinned);
      let st = resolveObjWordStyle(config, o.style, wid, lit);
      st = applyFontMode(config, j, lit, st);
      if (!single) st = { ...st, fontSize: (st.fontSize || 90) * 0.82 };
      if (wordByWord) {
        const wsStart = +(w.start ?? start);
        const reveal = enterDur > 0 ? clamp((t - wsStart) / enterDur) : (t >= wsStart ? 1 : 0);
        st = { ...st, opacity: (st.opacity ?? 1) * reveal };
      }
      return { text: applyCase(w.word, st.textCase, j), style: st };
    });
    let cx = W * (o.posX != null ? o.posX : (config.posX ?? 0.5));
    let cy = o.posY != null
      ? H * o.posY
      : (o.position ? H * (POS_Y[o.position] ?? 0.72)
        : H * (config.posY != null ? config.posY : (POS_Y[config.position] ?? 0.72)));
    // Object tracking → follow the tracked path; "move with camera" → ride the video's camera move; then float/shake.
    const tp = trackPos(o.track, t);
    if (tp) { cx = tp[0] * W; cy = tp[1] * H; }
    if (o.stick) { const cam = camAt(t); if (cam) { [cx, cy] = camPoint(cam, cx, cy, W, H); tf.scale *= cam.scale; tf.rot += cam.rot || 0; } }
    const mo = motionOffset(o.motion, t, W);
    if (mo) { tf.dx += mo.dx; tf.dy += mo.dy; tf.rot += mo.rot; }
    const letterMode = (!single && (config.letterMode === "fade" || config.letterMode === "scale")) ? config.letterMode : "";
    if (letterMode) {
      const lst = resolveObjWordStyle(config, o.style, (ws[0] && ws[0].word_id), false);
      drawLetterBlock(ctx, o.text || ws.map((w) => w.word).join(" "), lst, letterMode, t, start, end, cx, cy, maxW, scale, lineHeight, align);
    } else {
      const ofx = opts.noFx ? null : fxOf(o, config);
      const drift = fxDrift(ofx, t, W);
      if (drift) { tf.dx += drift.dx; tf.dy += drift.dy; tf.scale *= drift.scale; }
      if (isKnockout(ofx)) drawKnockout(ctx, ofx, W, H, (c) => drawTokens(c, tokens, lineHeight, align, cx, cy, maxW, tf, scale));
      else drawTokens(ctx, tokens, lineHeight, align, cx, cy, maxW, tf, scale);
    }
    // record hit-box + selection geometry (steady-state size)
    const geom = objectGeometry(ctx, o, W, H, config, scale, maxW, lineHeight);
    const tfx = opts.noFx ? null : fxOf(o, config);
    if (tfx && tf.alpha > 0.05) drawFxText(ctx, tfx, t, W, H, { x: geom.cx - geom.width / 2, y: geom.cy - geom.height / 2, w: geom.width, h: geom.height }, start, t <= end, beat);
    if (hitboxes) hitboxes.push({ layerId: o.__layerId, objId: o.__objId, cx: geom.cx, cy: geom.cy, w: geom.width, h: geom.height });
    if (opts.selectedId && o.__objId === opts.selectedId) selectedGeom = geom;
  });

  if (fxOn) fxs.forEach((fx) => drawFxFront(ctx, fx, t, W, H, energy));
  drawEmoji(ctx, t, W, H, config, segments);

  // Selected object not visible at this time? Still show a draggable box at its
  // position so the user can reposition it (measure with steady-state styles).
  if (opts.selectedId && !selectedGeom) {
    const so = objects.find((o) => o.__objId === opts.selectedId);
    if (so) {
      const geom = objectGeometry(ctx, so, W, H, config, scale, maxW, lineHeight);
      selectedGeom = geom;
      if (hitboxes && !hitboxes.some((b) => b.objId === opts.selectedId)) {
        hitboxes.push({ layerId: so.__layerId, objId: so.__objId, cx: geom.cx, cy: geom.cy, w: geom.width, h: geom.height });
      }
    }
  }
  if (selectedGeom) drawSelectionBox(ctx, selectedGeom.cx, selectedGeom.cy, selectedGeom.width, selectedGeom.height, W);
}

export { totalDuration };
